import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  createManualOrderWithLabel,
  getManualOrderCouriers,
  listVendors,
} from '../../services/shipturtleApi';
import { showToast } from '../../utils/toast';
import {
  codAmountError,
  hsnError,
  packageErrors,
  partyErrors,
  positiveNumberError,
  prefixKeys,
} from '../../utils/shippingValidation';
import { describeQuote, summarizeQuotesForTara } from '../../utils/prozoChannels';
import { useTaraTerminalFocus } from '../../utils/taraTerminalContext';
import RefreshRatesButton, { QuotesSkeleton } from '../TerminalLabels/RefreshRatesButton';
import PartyLookup from './PartyLookup';
import '../TerminalLabels/ManageLabels.css';
import './CreateManualOrder.css';

/**
 * Manual (off-Shopify) order capture.
 *
 * Mirrors the exception-label wizard step for step, with one structural
 * difference: that flow *selects* an existing order, this one *captures* a whole
 * order, because there is nothing in the terminal to select. Everything the
 * label needs — customer, pickup, lines, package — is typed here.
 *
 * The order and its label are one request: the server saves the order only if
 * Prozo issues the AWB, committing the row and the AWB together. A refusal —
 * ours or Prozo's — rolls everything back, so a failed attempt leaves no draft
 * behind and the corrected form is simply submitted again.
 */

const STEPS = [
  'Customer Details',
  'Pickup Details',
  'Order Details',
  'Package Details',
  'Charge & Courier',
  'Review',
];

const emptyAddress = () => ({
  address_line_1: '',
  address_line_2: '',
  landmark: '',
  city: '',
  state: '',
  pincode: '',
  country: 'India',
  gstin: '',
});

/**
 * An address-book address as the form holds it. `gstin` is blanked: neither
 * address block shows a GSTIN input (the pickup's lives on the contact), so a
 * prefilled value there would be sent without anyone seeing it.
 */
const prefilledAddress = (address) => ({ ...emptyAddress(), ...(address || {}), gstin: '' });

// What a prefill writes on each step, marked touched so a value the address
// book holds but the courier would refuse is flagged at once, not at Next.
const RECIPIENT_KEYS = [
  'customer.name',
  'customer.phone',
  'customer.email',
  'to.address_line_1',
  'to.pincode',
  'to.city',
  'to.state',
];
const PICKUP_KEYS = [
  'pickup.name',
  'pickup.phone',
  'pickup.email',
  'pickup.gstin',
  'from.address_line_1',
  'from.pincode',
  'from.city',
  'from.state',
];

// The recipient can be anyone already known — a customer, or a vendor when
// Suspire ships to one. Goods only ever leave a vendor's warehouse (Suspire
// included, as a vendor), so the pickup offers vendors alone.
const RECIPIENT_KINDS = [
  ['customer', 'Customer'],
  ['vendor', 'Vendor'],
];
const PICKUP_KINDS = [['vendor', 'Vendor']];

const emptyLine = () => ({
  sku: '',
  product_name: '',
  quantity: 1,
  unit_price: '',
  hsn_code: '',
  gst_rate: '',
  discount: '',
  // Per line, not shared: opening the extras on one row must not open them on
  // every other row. Stripped before the payload is sent.
  showExtras: false,
});

/**
 * Volumetric weight in grams — the same divisor the other label screens use.
 * Couriers bill on whichever is greater, actual or volumetric, so the operator
 * sees it before committing to a package size.
 */
const volumetricWeightGrams = (length, breadth, height) => {
  const [l, b, h] = [length, breadth, height].map((v) => parseFloat(v));
  if (![l, b, h].every((v) => Number.isFinite(v) && v > 0)) return 0;
  return (l * b * h * 1000) / 5000;
};

const formatGrams = (grams) => {
  if (!Number.isFinite(grams) || grams <= 0) return '0 g';
  return `${Number.isInteger(grams) ? grams : Number(grams.toFixed(2))} g`;
};

const money = (value) => {
  const n = parseFloat(value);
  return Number.isFinite(n) ? n : 0;
};

// Party fields that belong to the contact (shown with the contact inputs)
// rather than the address block.
const CONTACT_FIELDS = new Set(['name', 'phone', 'email', 'gstin']);

/** Split partyErrors() into contact keys and address keys for the form. */
const splitPartyErrors = (errors, contactPrefix, addressPrefix) =>
  Object.fromEntries(
    Object.entries(errors).map(([field, message]) => [
      `${CONTACT_FIELDS.has(field) ? contactPrefix : addressPrefix}.${field}`,
      message,
    ])
  );

// Server field names -> the form's error keys.
const SERVER_FIELD_KEYS = {
  customer_name: 'customer.name',
  customer_phone: 'customer.phone',
  customer_email: 'customer.email',
  pickup_contact_name: 'pickup.name',
  pickup_contact_phone: 'pickup.phone',
  pickup_contact_email: 'pickup.email',
  pickup_gstin: 'pickup.gstin',
  order_number: 'orderNumber',
  invoice_value: 'invoiceValue',
  cod_amount: 'codAmount',
  weight_g: 'pkg.weight_g',
  length_cm: 'pkg.length_cm',
  breadth_cm: 'pkg.breadth_cm',
  height_cm: 'pkg.height_cm',
  charge_bearer: 'chargeBearer',
  vendor: 'vendor',
  courier_channel: 'courier',
  notes: 'notes',
};

/** Which wizard step an error key lives on. */
const stepForKey = (key) => {
  if (key.startsWith('customer.') || key.startsWith('to.')) return 0;
  if (key.startsWith('pickup.') || key.startsWith('from.')) return 1;
  if (['orderNumber', 'invoiceValue', 'codAmount', 'lines'].includes(key) || key.startsWith('line.')) {
    return 2;
  }
  if (key.startsWith('pkg.')) return 3;
  if (['chargeBearer', 'vendor', 'courier'].includes(key)) return 4;
  return 5;
};

/**
 * A refusal from the server, as form error keys: serializer `errors` (nested
 * for addresses and line items) on save, `field_errors` (ship_from.phone …)
 * from the label check.
 */
const mapServerErrors = (err) => {
  const out = {};
  const first = (value) => {
    if (typeof value === 'string') return value;
    if (Array.isArray(value) && typeof value[0] === 'string') return value[0];
    return null;
  };
  Object.entries(err?.errors || {}).forEach(([field, value]) => {
    if (field === 'shipping_address' || field === 'pickup_address') {
      const prefix = field === 'shipping_address' ? 'to' : 'from';
      Object.entries(value || {}).forEach(([sub, messages]) => {
        const message = first(messages);
        if (message) out[`${prefix}.${sub}`] = message;
      });
    } else if (field === 'line_items' && Array.isArray(value) && typeof value[0] === 'object') {
      value.forEach((lineErrors, index) =>
        Object.entries(lineErrors || {}).forEach(([sub, messages]) => {
          const message = first(messages);
          if (message) out[`line.${index}.${sub}`] = message;
        })
      );
    } else {
      const message = first(value);
      if (message) out[field === 'line_items' ? 'lines' : SERVER_FIELD_KEYS[field] || field] = message;
    }
  });
  Object.entries(err?.fieldErrors || {}).forEach(([path, message]) => {
    const [side, field] = path.split('.');
    if (side === 'ship_from') out[`${CONTACT_FIELDS.has(field) ? 'pickup' : 'from'}.${field}`] = message;
    else if (side === 'ship_to') out[`${CONTACT_FIELDS.has(field) ? 'customer' : 'to'}.${field}`] = message;
    else out[SERVER_FIELD_KEYS[path] || path] = message;
  });
  return out;
};

function CreateManualOrder({ onDone }) {
  const navigate = useNavigate();

  // A clone arrives as navigation state from the manual-orders list. Read once
  // into a ref: it is a one-shot prefill, and leaving it in `location` would
  // re-apply it on every render.
  const location = useLocation();
  const clone = useRef(location.state?.cloneFrom || null).current;
  const clonedFrom = clone?.cloned_from || null;

  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  // Errors show for a field once it is left (touched), or for the whole step
  // once Next is pressed on it (revealed) — never on a form not yet typed in.
  const [touched, setTouched] = useState({});
  const [revealed, setRevealed] = useState({});
  // What the server refused on the last submit, keyed like the client errors.
  const [serverErrors, setServerErrors] = useState({});

  // --- Step 1: customer -----------------------------------------------------
  const [customer, setCustomer] = useState({
    name: clone?.customer_name || '',
    phone: clone?.customer_phone || '',
    email: clone?.customer_email || '',
  });
  const [shipTo, setShipTo] = useState(
    clone?.shipping_address ? { ...emptyAddress(), ...clone.shipping_address } : emptyAddress()
  );

  // Address-book picks: { entry, addressIndex }. A prefill source only — never
  // sent; the order stores whatever the fields hold at submit.
  const [recipientKind, setRecipientKind] = useState('customer');
  const [recipientPick, setRecipientPick] = useState(null);

  // --- Step 2: pickup -------------------------------------------------------
  const [pickupContact, setPickupContact] = useState({
    name: clone?.pickup_contact_name || '',
    phone: clone?.pickup_contact_phone || '',
    email: clone?.pickup_contact_email || '',
    gstin: clone?.pickup_gstin || '',
  });
  const [shipFrom, setShipFrom] = useState(
    clone?.pickup_address ? { ...emptyAddress(), ...clone.pickup_address } : emptyAddress()
  );
  const [pickupPick, setPickupPick] = useState(null);

  // --- Step 3: order --------------------------------------------------------
  const [orderNumber, setOrderNumber] = useState('');
  // Carried over on a clone: a re-ship of the same goods to the same customer
  // collects the same way, and the amount is one the operator already worked
  // out. Both remain editable.
  const [paymentMode, setPaymentMode] = useState(clone?.payment_mode || 'PREPAID');
  const [codAmount, setCodAmount] = useState(clone?.cod_amount ?? '');
  const [invoiceValue, setInvoiceValue] = useState(clone?.invoice_value ?? '');
  const [invoiceNumber, setInvoiceNumber] = useState(clone?.invoice_number || '');
  const [clientOrderId, setClientOrderId] = useState(clone?.client_order_id || '');
  const [shippingLabelId, setShippingLabelId] = useState(clone?.shipping_label_id || '');
  const [lines, setLines] = useState(
    clone?.line_items?.length
      ? clone.line_items.map((li) => ({ ...emptyLine(), ...li }))
      : [emptyLine()]
  );

  // --- Step 4: package ------------------------------------------------------
  const [pkg, setPkg] = useState({
    weight_g: clone?.weight_g ?? '',
    length_cm: clone?.length_cm ?? '',
    breadth_cm: clone?.breadth_cm ?? '',
    height_cm: clone?.height_cm ?? '',
  });

  // --- Step 5: charge bearer + courier -------------------------------------
  // Who pays carries over: a standing arrangement with the vendor rather than a
  // per-shipment decision.
  const [chargeBearer, setChargeBearer] = useState(clone?.charge_bearer || '');
  const [vendorId, setVendorId] = useState(clone?.vendor || '');
  const [vendors, setVendors] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [quotesLoading, setQuotesLoading] = useState(false);
  const [quotesError, setQuotesError] = useState('');
  const [courier, setCourier] = useState(null);

  // --- Step 6: review -------------------------------------------------------
  const [notes, setNotes] = useState('');

  useTaraTerminalFocus('manual_order_wizard', {
    step: STEPS[step],
    order_number: orderNumber || undefined,
    ship_to: [shipTo.city, shipTo.state, shipTo.pincode].filter(Boolean).join(', ') || undefined,
    ship_from: [shipFrom.city, shipFrom.state, shipFrom.pincode].filter(Boolean).join(', ') || undefined,
    payment_mode: paymentMode,
    items: lines
      .filter((l) => l.sku || l.product_name)
      .map((l) => ({ sku: l.sku, product: l.product_name, qty: l.quantity, unit_price: l.unit_price })),
    package: pkg,
    charge_bearer: chargeBearer || undefined,
    selected_courier: courier || undefined,
    courier_quotes: summarizeQuotesForTara(quotes),
  });

  // Vendors are only needed when the brand bears the charge, so they are
  // fetched on demand rather than on mount.
  const vendorsLoaded = useRef(false);
  useEffect(() => {
    if (chargeBearer !== 'BRAND' || vendorsLoaded.current) return;
    vendorsLoaded.current = true;
    (async () => {
      try {
        const data = await listVendors({ limit: 500 });
        setVendors(data?.vendors || data?.results || []);
      } catch {
        showToast('Could not load vendors', 'error');
      }
    })();
  }, [chargeBearer]);

  // Choosing Suspire clears any vendor already picked: the server refuses a
  // Suspire-borne order that carries one, since it would look billable.
  useEffect(() => {
    if (chargeBearer === 'SUSPIRE') setVendorId('');
  }, [chargeBearer]);

  const declaredTotal = useMemo(
    () =>
      lines.reduce(
        (sum, l) => sum + Math.max(0, money(l.unit_price) * (parseInt(l.quantity, 10) || 0) - money(l.discount)),
        0
      ),
    [lines]
  );

  const volumetric = volumetricWeightGrams(pkg.length_cm, pkg.breadth_cm, pkg.height_cm);
  const chargeable = Math.max(volumetric, parseFloat(pkg.weight_g) || 0);

  // --- Validation -----------------------------------------------------------
  // The rules in utils/shippingValidation.js mirror the server's, so a step
  // that passes here will not be refused at Generate Label for the same field.
  // The server re-checks everything; this is for an instant, per-field message.
  const stepErrors = useCallback(
    (index) => {
      switch (index) {
        case 0:
          return splitPartyErrors(
            partyErrors(
              { ...shipTo, name: customer.name, phone: customer.phone, email: customer.email, gstin: '' },
              { who: 'Customer' }
            ),
            'customer',
            'to'
          );
        case 1:
          // Pickup email is required because Prozo's `from_email` is: without
          // it /order/create fails with "Input Data Error:From Email is
          // required" only once the operator hits Generate Label.
          return splitPartyErrors(
            partyErrors(
              {
                ...shipFrom,
                name: pickupContact.name,
                phone: pickupContact.phone,
                email: pickupContact.email,
                gstin: pickupContact.gstin,
              },
              { requireEmail: true, who: 'Pickup' }
            ),
            'pickup',
            'from'
          );
        case 2: {
          const errors = {};
          if (!orderNumber.trim()) errors.orderNumber = 'Order number is required.';
          const invoiceError = positiveNumberError(invoiceValue, 'Invoice value');
          if (invoiceError) errors.invoiceValue = invoiceError;
          if (paymentMode === 'COD') {
            const codError = codAmountError(codAmount, invoiceValue);
            if (codError) errors.codAmount = codError;
          }
          if (!lines.length) errors.lines = 'At least one line item is required.';
          lines.forEach((l, i) => {
            if (!String(l.sku || '').trim()) errors[`line.${i}.sku`] = 'SKU is required.';
            if (!String(l.product_name || '').trim()) {
              errors[`line.${i}.product_name`] = 'Product name is required.';
            }
            const qty = Number(String(l.quantity).trim());
            if (!Number.isInteger(qty) || qty < 1) {
              errors[`line.${i}.quantity`] = 'Quantity must be a whole number, at least 1.';
            }
            const price = String(l.unit_price ?? '').trim();
            if (!price) errors[`line.${i}.unit_price`] = 'Unit price is required.';
            else if (!Number.isFinite(Number(price))) errors[`line.${i}.unit_price`] = 'Unit price must be a number.';
            else if (Number(price) < 0) errors[`line.${i}.unit_price`] = 'Unit price cannot be negative.';
            const hsn = hsnError(l.hsn_code);
            if (hsn) errors[`line.${i}.hsn_code`] = hsn;
          });
          return errors;
        }
        case 3:
          return prefixKeys(packageErrors(pkg), 'pkg');
        case 4: {
          const errors = {};
          if (!chargeBearer) errors.chargeBearer = 'Choose who bears the shipping charge.';
          if (chargeBearer === 'BRAND' && !vendorId) errors.vendor = 'Choose the vendor to bill.';
          if (!courier) errors.courier = 'Select a courier.';
          return errors;
        }
        default:
          return {};
      }
    },
    [customer, shipTo, pickupContact, shipFrom, orderNumber, invoiceValue, paymentMode,
     codAmount, lines, pkg, chargeBearer, vendorId, courier]
  );

  const stepValid = useCallback((index) => Object.keys(stepErrors(index)).length === 0, [stepErrors]);

  const currentErrors = useMemo(() => stepErrors(step), [stepErrors, step]);

  /** The message to show under a field, or null. */
  const fieldErr = (key) =>
    ((revealed[step] || touched[key]) && currentErrors[key]) || serverErrors[key] || null;
  const fieldClass = (key, extra = '') =>
    ['field', extra, fieldErr(key) ? 'fieldError' : ''].filter(Boolean).join(' ');
  const errorText = (key) => {
    const message = fieldErr(key);
    return message ? (
      <small className="fieldErrorText" role="alert">
        {message}
      </small>
    ) : null;
  };
  const touch = (key) => () => setTouched((prev) => (prev[key] ? prev : { ...prev, [key]: true }));
  const setTouchedKeys = (keys, value) =>
    setTouched((prev) => ({ ...prev, ...Object.fromEntries(keys.map((k) => [k, value])) }));

  // --- Address-book prefill -------------------------------------------------
  // Every field is overwritten, blanks included: a value left over from a
  // previous pick would belong to someone else.
  const applyRecipient = (entry, addressIndex = 0) => {
    setCustomer({ name: entry.name || '', phone: entry.phone || '', email: entry.email || '' });
    setShipTo(prefilledAddress(entry.addresses?.[addressIndex]));
    setRecipientPick({ entry, addressIndex });
    setTouchedKeys(RECIPIENT_KEYS, true);
  };

  const clearRecipient = () => {
    setCustomer({ name: '', phone: '', email: '' });
    setShipTo(emptyAddress());
    setRecipientPick(null);
    setTouchedKeys(RECIPIENT_KEYS, false);
  };

  const applyPickup = (entry) => {
    const address = entry.addresses?.[0];
    setPickupContact({
      // Vendor entries carry the warehouse name as `name`.
      name: entry.name || entry.contact_name || '',
      phone: entry.phone || '',
      email: entry.email || '',
      gstin: address?.gstin || entry.gstin || '',
    });
    setShipFrom(prefilledAddress(address));
    setPickupPick({ entry, addressIndex: 0 });
    setTouchedKeys(PICKUP_KEYS, true);
  };

  const clearPickup = () => {
    setPickupContact({ name: '', phone: '', email: '', gstin: '' });
    setShipFrom(emptyAddress());
    setPickupPick(null);
    setTouchedKeys(PICKUP_KEYS, false);
  };

  const goNext = () => {
    const count = Object.keys(currentErrors).length;
    if (count) {
      setRevealed((prev) => ({ ...prev, [step]: true }));
      showToast(`Fix ${count} ${count === 1 ? 'field' : 'fields'} before continuing.`, 'error');
      return;
    }
    // The operator has corrected this step; earlier server refusals for it
    // no longer describe what is on screen.
    setServerErrors((prev) =>
      Object.fromEntries(Object.entries(prev).filter(([key]) => stepForKey(key) !== step))
    );
    setStep(step + 1);
  };

  // Notes are required. Asked at the final step deliberately — written with the
  // whole order in view rather than guessed at the start — and enforced again
  // by the serializer, so the form is a convenience rather than the guard.
  const canSubmit = notes.trim().length > 0 && !submitting;

  // --- Courier quotes -------------------------------------------------------
  const fetchQuotes = useCallback(async () => {
    setQuotesLoading(true);
    setQuotesError('');
    // Clear the previous rates before the request, not after it: a stale price
    // shown next to a spinner invites picking a rate that is about to change.
    // The selection goes too, since the quote behind it is gone.
    setQuotes([]);
    setCourier(null);
    try {
      const data = await getManualOrderCouriers({
        from_pincode: shipFrom.pincode,
        to_pincode: shipTo.pincode,
        length_cm: pkg.length_cm,
        breadth_cm: pkg.breadth_cm,
        height_cm: pkg.height_cm,
        weight_g: pkg.weight_g,
        payment_mode: paymentMode,
        cod_amount: paymentMode === 'COD' ? codAmount : 0,
      });
      setQuotes(data?.serviceable_options || []);
      if (!(data?.serviceable_options || []).length) {
        setQuotesError('No couriers serve this lane for the given package.');
      }
    } catch (err) {
      setQuotesError(err?.message || 'Could not fetch couriers.');
      setQuotes([]);
    } finally {
      setQuotesLoading(false);
    }
  }, [shipFrom.pincode, shipTo.pincode, pkg, paymentMode, codAmount]);

  // Quote as soon as the step opens — a courier must be chosen, and the list is
  // the step, so making the operator press a button first is pure friction.
  useEffect(() => {
    if (step === 4 && quotes.length === 0 && !quotesLoading && !quotesError) {
      fetchQuotes();
    }
  }, [step, quotes.length, quotesLoading, quotesError, fetchQuotes]);

  // --- Submit ---------------------------------------------------------------
  const handleSubmit = async () => {
    // Re-check every step: the stepper lets the operator go back and edit, so a
    // step passed earlier may no longer be valid.
    const firstInvalid = [0, 1, 2, 3, 4].find((index) => !stepValid(index));
    if (firstInvalid !== undefined) {
      setRevealed((prev) => ({ ...prev, [firstInvalid]: true }));
      setStep(firstInvalid);
      showToast(`Fix the highlighted fields in ${STEPS[firstInvalid]}.`, 'error');
      return;
    }
    setSubmitting(true);
    setError('');
    setServerErrors({});
    try {
      const payload = {
        order_number: orderNumber.trim(),
        charge_bearer: chargeBearer,
        // Only sent when the brand bears it; the server rejects a vendor on a
        // Suspire-borne order.
        ...(chargeBearer === 'BRAND' ? { vendor: vendorId } : {}),
        notes: notes.trim(),
        client_order_id: clientOrderId.trim(),
        shipping_label_id: shippingLabelId.trim(),
        customer_name: customer.name.trim(),
        customer_phone: customer.phone.trim(),
        customer_email: customer.email.trim(),
        shipping_address: shipTo,
        pickup_address: shipFrom,
        pickup_contact_name: pickupContact.name.trim(),
        pickup_contact_phone: pickupContact.phone.trim(),
        pickup_contact_email: pickupContact.email.trim(),
        pickup_gstin: pickupContact.gstin.trim(),
        payment_mode: paymentMode,
        ...(paymentMode === 'COD' ? { cod_amount: codAmount } : {}),
        invoice_value: invoiceValue,
        invoice_number: invoiceNumber.trim(),
        weight_g: parseInt(pkg.weight_g, 10),
        length_cm: pkg.length_cm,
        breadth_cm: pkg.breadth_cm,
        height_cm: pkg.height_cm,
        // `courier` already holds the bookable channel string that describeQuote
        // resolved (e.g. "bluedart-air"), not the raw quote row.
        courier_channel: courier || '',
        // `showExtras` is UI state only and is deliberately not sent: it is not
        // a model field, and the serializer rejects unknown keys.
        line_items: lines.map((l, index) => ({
          sku: l.sku.trim(),
          product_name: l.product_name.trim(),
          quantity: parseInt(l.quantity, 10),
          unit_price: l.unit_price,
          hsn_code: l.hsn_code.trim(),
          ...(String(l.gst_rate).trim() ? { gst_rate: l.gst_rate } : {}),
          ...(String(l.discount).trim() ? { discount: l.discount } : {}),
          position: index + 1,
        })),
      };

      // One call: the server saves the order only if Prozo issues the AWB, so
      // a refusal leaves nothing behind and the corrected form is resubmitted
      // as-is.
      const labelled = await createManualOrderWithLabel(payload);

      if (labelled?.label_pending) {
        showToast(
          `AWB ${labelled.awb_number} created. The label PDF is not ready yet — retry the download from the list.`,
          'warning'
        );
      } else {
        showToast(`Label generated — AWB ${labelled.awb_number}`, 'success');
      }
      if (onDone) onDone();
      else navigate('/suspire-terminal/orders?section=manual');
    } catch (err) {
      // Nothing was saved — validation and Prozo refusals both roll back — so
      // the operator corrects the marked fields and submits again.
      const mapped = mapServerErrors(err);
      const keys = Object.keys(mapped);
      const message = err?.message || 'Something went wrong.';
      setError(message);
      if (keys.length) {
        // Take the operator to the earliest step the server refused, with
        // each refused field marked.
        setServerErrors(mapped);
        const target = Math.min(...keys.map(stepForKey));
        setRevealed((prev) => ({ ...prev, [target]: true }));
        if (target < STEPS.length - 1) setStep(target);
      }
      showToast(message, 'error');
    } finally {
      setSubmitting(false);
    }
  };

  // --- Renderers ------------------------------------------------------------
  const addressFields = (value, setValue, prefix) => (
    <>
      <div className="gridFull">
        <label className={fieldClass(`${prefix}.address_line_1`)}>
          <span>Complete Address *</span>
          <input
            value={value.address_line_1}
            onChange={(e) => setValue({ ...value, address_line_1: e.target.value })}
            onBlur={touch(`${prefix}.address_line_1`)}
            placeholder="Flat / building / street"
          />
          {errorText(`${prefix}.address_line_1`)}
        </label>
      </div>
      <div className="gridFull">
        <label className="field">
          <span>Address Line 2</span>
          <input
            value={value.address_line_2}
            onChange={(e) => setValue({ ...value, address_line_2: e.target.value })}
          />
        </label>
      </div>
      <label className={fieldClass(`${prefix}.pincode`)}>
        <span>Pincode *</span>
        <input
          value={value.pincode}
          onChange={(e) => setValue({ ...value, pincode: e.target.value })}
          onBlur={touch(`${prefix}.pincode`)}
          inputMode="numeric"
          maxLength={6}
        />
        {errorText(`${prefix}.pincode`)}
      </label>
      <label className={fieldClass(`${prefix}.city`)}>
        <span>City *</span>
        <input
          value={value.city}
          onChange={(e) => setValue({ ...value, city: e.target.value })}
          onBlur={touch(`${prefix}.city`)}
        />
        {errorText(`${prefix}.city`)}
      </label>
      <label className={fieldClass(`${prefix}.state`)}>
        <span>State *</span>
        <input
          value={value.state}
          onChange={(e) => setValue({ ...value, state: e.target.value })}
          onBlur={touch(`${prefix}.state`)}
        />
        {errorText(`${prefix}.state`)}
      </label>
      {prefix === 'from' && (
        <label className={fieldClass('pickup.gstin')}>
          <span>GSTIN</span>
          <input
            value={pickupContact.gstin}
            onChange={(e) => setPickupContact({ ...pickupContact, gstin: e.target.value })}
            onBlur={touch('pickup.gstin')}
            maxLength={15}
          />
          {errorText('pickup.gstin')}
        </label>
      )}
    </>
  );

  const renderCustomer = () => (
    <>
      <p className="helpText">Who is the recipient, and where is the order going?</p>
      <PartyLookup
        title="Fill from an existing customer or vendor"
        kinds={RECIPIENT_KINDS}
        kind={recipientKind}
        onKindChange={setRecipientKind}
        picked={recipientPick}
        onPick={(entry) => applyRecipient(entry)}
        onAddress={(index) => applyRecipient(recipientPick.entry, index)}
        onClear={clearRecipient}
      />
      <div className="grid">
        <label className={fieldClass('customer.name')}>
          <span>Name *</span>
          <input
            value={customer.name}
            onChange={(e) => setCustomer({ ...customer, name: e.target.value })}
            onBlur={touch('customer.name')}
          />
          {errorText('customer.name')}
        </label>
        <label className={fieldClass('customer.phone')}>
          <span>Phone Number *</span>
          <input
            value={customer.phone}
            onChange={(e) => setCustomer({ ...customer, phone: e.target.value })}
            onBlur={touch('customer.phone')}
            inputMode="tel"
            placeholder="10-digit mobile, e.g. 9876543210"
          />
          {errorText('customer.phone')}
        </label>
        <label className={fieldClass('customer.email')}>
          <span>Email Address</span>
          <input
            type="email"
            value={customer.email}
            onChange={(e) => setCustomer({ ...customer, email: e.target.value })}
            onBlur={touch('customer.email')}
          />
          {errorText('customer.email')}
        </label>
        {addressFields(shipTo, setShipTo, 'to')}
      </div>
    </>
  );

  const renderPickup = () => (
    <>
      <p className="helpText">
        Where is the parcel collected from? Fill it from a vendor&apos;s warehouse or type it in
        — either way the order keeps its own copy and stays unlinked from the warehouse.
      </p>
      <PartyLookup
        title="Fill from a vendor warehouse (Suspire included)"
        kinds={PICKUP_KINDS}
        kind="vendor"
        onKindChange={() => {}}
        picked={pickupPick}
        onPick={applyPickup}
        onAddress={() => {}}
        onClear={clearPickup}
      />
      <div className="grid">
        <label className={fieldClass('pickup.name')}>
          <span>Contact Person *</span>
          <input
            value={pickupContact.name}
            onChange={(e) => setPickupContact({ ...pickupContact, name: e.target.value })}
            onBlur={touch('pickup.name')}
            placeholder="Person's name, e.g. Sachin"
          />
          {errorText('pickup.name')}
        </label>
        <label className={fieldClass('pickup.phone')}>
          <span>Contact Number *</span>
          <input
            value={pickupContact.phone}
            onChange={(e) => setPickupContact({ ...pickupContact, phone: e.target.value })}
            onBlur={touch('pickup.phone')}
            inputMode="tel"
            placeholder="10-digit mobile, e.g. 9876543210"
          />
          {errorText('pickup.phone')}
        </label>
        <label className={fieldClass('pickup.email')}>
          <span>Contact Email *</span>
          <input
            type="email"
            required
            value={pickupContact.email}
            onChange={(e) => setPickupContact({ ...pickupContact, email: e.target.value })}
            onBlur={touch('pickup.email')}
          />
          {errorText('pickup.email')}
        </label>
        {addressFields(shipFrom, setShipFrom, 'from')}
      </div>
    </>
  );

  const updateLine = (index, patch) =>
    setLines((prev) => prev.map((l, i) => (i === index ? { ...l, ...patch } : l)));

  const renderOrder = () => (
    <>
      <div className="grid">
        <label className={fieldClass('orderNumber')}>
          <span>Order Number *</span>
          <input
            value={orderNumber}
            onChange={(e) => setOrderNumber(e.target.value)}
            onBlur={touch('orderNumber')}
            placeholder="e.g. EXPO-2026-014"
          />
          {errorText('orderNumber') || (
            <small className="hint">Free-form, but must be unique across manual orders.</small>
          )}
        </label>
        <label className="field">
          <span>Payment Mode</span>
          <select value={paymentMode} onChange={(e) => setPaymentMode(e.target.value)}>
            <option value="PREPAID">Prepaid</option>
            <option value="COD">COD</option>
          </select>
        </label>
        {paymentMode === 'COD' && (
          <label className={fieldClass('codAmount')}>
            <span>COD Amount *</span>
            <input
              value={codAmount}
              onChange={(e) => setCodAmount(e.target.value)}
              onBlur={touch('codAmount')}
              inputMode="decimal"
            />
            {errorText('codAmount') || (
              <small className="hint">What the courier collects on delivery.</small>
            )}
          </label>
        )}
        <label className={fieldClass('invoiceValue')}>
          <span>Invoice Value *</span>
          <input
            value={invoiceValue}
            onChange={(e) => setInvoiceValue(e.target.value)}
            onBlur={touch('invoiceValue')}
            inputMode="decimal"
          />
          {errorText('invoiceValue') || (
            <small className="hint">Declared value — what the courier insures.</small>
          )}
        </label>
        <label className="field">
          <span>Invoice Number</span>
          <input value={invoiceNumber} onChange={(e) => setInvoiceNumber(e.target.value)} />
        </label>
        <label className="field">
          <span>Client Order ID</span>
          <input value={clientOrderId} onChange={(e) => setClientOrderId(e.target.value)} />
        </label>
        <label className="field">
          <span>Shipping Label ID</span>
          <input value={shippingLabelId} onChange={(e) => setShippingLabelId(e.target.value)} />
        </label>
      </div>

      <div className="manualLines">
        <div className="manualLinesHeader">
          <h4>Line Items</h4>
          <span className="manualLinesCount">
            {lines.length} {lines.length === 1 ? 'line' : 'lines'}
          </span>
        </div>

        {lines.map((line, index) => {
          const lineTotal = Math.max(
            0,
            money(line.unit_price) * (parseInt(line.quantity, 10) || 0) - money(line.discount)
          );
          return (
            <div className="manualLineCard" key={index}>
              <div className="manualLineCardHead">
                <span className="manualLineBadge">{index + 1}</span>
                <span className="manualLineTitle">
                  {line.product_name?.trim() || line.sku?.trim() || 'New line item'}
                </span>
                {lineTotal > 0 && (
                  <span className="manualLineAmount">₹{lineTotal.toFixed(2)}</span>
                )}
                {lines.length > 1 && (
                  <button
                    type="button"
                    className="manualLineRemove"
                    aria-label={`Remove line ${index + 1}`}
                    title="Remove this line"
                    onClick={() => setLines((prev) => prev.filter((_, i) => i !== index))}
                  >
                    ×
                  </button>
                )}
              </div>

              <div className="manualLineFields">
                <label className={fieldClass(`line.${index}.sku`)}>
                  <span>SKU ID *</span>
                  <input
                    value={line.sku}
                    onChange={(e) => updateLine(index, { sku: e.target.value })}
                    onBlur={touch(`line.${index}.sku`)}
                    placeholder="e.g. RING-01"
                  />
                  {errorText(`line.${index}.sku`)}
                </label>
                <label className={fieldClass(`line.${index}.product_name`)}>
                  <span>Name *</span>
                  <input
                    value={line.product_name}
                    onChange={(e) => updateLine(index, { product_name: e.target.value })}
                    onBlur={touch(`line.${index}.product_name`)}
                    placeholder="Product name"
                  />
                  {errorText(`line.${index}.product_name`)}
                </label>
                <label className={fieldClass(`line.${index}.quantity`, 'manualFieldNarrow')}>
                  <span>Quantity *</span>
                  <input
                    type="number"
                    min="1"
                    value={line.quantity}
                    onChange={(e) => updateLine(index, { quantity: e.target.value })}
                    onBlur={touch(`line.${index}.quantity`)}
                  />
                  {errorText(`line.${index}.quantity`)}
                </label>
                <label className={fieldClass(`line.${index}.unit_price`, 'manualFieldNarrow')}>
                  <span>Unit Price *</span>
                  <input
                    value={line.unit_price}
                    onChange={(e) => updateLine(index, { unit_price: e.target.value })}
                    onBlur={touch(`line.${index}.unit_price`)}
                    inputMode="decimal"
                    placeholder="0.00"
                  />
                  {errorText(`line.${index}.unit_price`)}
                </label>
              </div>

              {/* Per line: opening the extras here leaves every other line
                  untouched, since the flag lives on the line itself. */}
              <button
                type="button"
                className="manualLineExtrasToggle"
                aria-expanded={Boolean(line.showExtras)}
                onClick={() => updateLine(index, { showExtras: !line.showExtras })}
              >
                <span className="manualLineExtrasIcon">{line.showExtras ? '−' : '+'}</span>
                {line.showExtras ? 'Hide' : 'Add'} HSN code, GST and discount
              </button>

              {(line.showExtras || fieldErr(`line.${index}.hsn_code`)) && (
                <div className="manualLineExtras">
                  <label className={fieldClass(`line.${index}.hsn_code`)}>
                    <span>HSN Code</span>
                    <input
                      value={line.hsn_code}
                      onChange={(e) => updateLine(index, { hsn_code: e.target.value })}
                      onBlur={touch(`line.${index}.hsn_code`)}
                      placeholder="Optional — 4, 6 or 8 digits"
                    />
                    {errorText(`line.${index}.hsn_code`)}
                  </label>
                  <label className="field">
                    <span>GST %</span>
                    <input
                      value={line.gst_rate}
                      onChange={(e) => updateLine(index, { gst_rate: e.target.value })}
                      inputMode="decimal"
                      placeholder="0"
                    />
                  </label>
                  <label className="field">
                    <span>Discount (₹)</span>
                    <input
                      value={line.discount}
                      onChange={(e) => updateLine(index, { discount: e.target.value })}
                      inputMode="decimal"
                      placeholder="0"
                    />
                  </label>
                </div>
              )}
            </div>
          );
        })}

        {errorText('lines')}
        <div className="manualLinesFooter">
          <button
            type="button"
            className="btn btnGhost"
            onClick={() => setLines((prev) => [...prev, emptyLine()])}
          >
            + Add another line
          </button>
          <span className="manualLinesTotal">
            Declared total: <strong>₹{declaredTotal.toFixed(2)}</strong>
          </span>
        </div>
      </div>
    </>
  );

  const renderPackage = () => (
    <>
      <p className="helpText">
        Dimensions in centimetres, weight in grams — the units the courier uses.
      </p>
      <div className="grid">
        <label className={fieldClass('pkg.weight_g')}>
          <span>Weight (gms) *</span>
          <input
            value={pkg.weight_g}
            onChange={(e) => setPkg({ ...pkg, weight_g: e.target.value })}
            onBlur={touch('pkg.weight_g')}
            inputMode="numeric"
          />
          {errorText('pkg.weight_g')}
        </label>
        <label className={fieldClass('pkg.length_cm')}>
          <span>Length (cm) *</span>
          <input
            value={pkg.length_cm}
            onChange={(e) => setPkg({ ...pkg, length_cm: e.target.value })}
            onBlur={touch('pkg.length_cm')}
            inputMode="decimal"
          />
          {errorText('pkg.length_cm')}
        </label>
        <label className={fieldClass('pkg.breadth_cm')}>
          <span>Breadth (cm) *</span>
          <input
            value={pkg.breadth_cm}
            onChange={(e) => setPkg({ ...pkg, breadth_cm: e.target.value })}
            onBlur={touch('pkg.breadth_cm')}
            inputMode="decimal"
          />
          {errorText('pkg.breadth_cm')}
        </label>
        <label className={fieldClass('pkg.height_cm')}>
          <span>Height (cm) *</span>
          <input
            value={pkg.height_cm}
            onChange={(e) => setPkg({ ...pkg, height_cm: e.target.value })}
            onBlur={touch('pkg.height_cm')}
            inputMode="decimal"
          />
          {errorText('pkg.height_cm')}
        </label>
      </div>
      {volumetric > 0 && (
        <p className="hint">
          Volumetric weight {formatGrams(volumetric)} · chargeable{' '}
          <strong>{formatGrams(chargeable)}</strong> — couriers bill on whichever is greater.
        </p>
      )}
    </>
  );

  const renderCourier = () => (
    <>
      <div className="grid">
        <label className={fieldClass('chargeBearer')}>
          <span>Charge To *</span>
          <select value={chargeBearer} onChange={(e) => setChargeBearer(e.target.value)}>
            <option value="">Select…</option>
            <option value="SUSPIRE">Suspire</option>
            <option value="BRAND">Brand</option>
          </select>
          {errorText('chargeBearer') || (
            <small className="hint">Who absorbs the courier charge for this shipment.</small>
          )}
        </label>
        {chargeBearer === 'BRAND' && (
          <label className={fieldClass('vendor')}>
            <span>Vendor *</span>
            <select value={vendorId} onChange={(e) => setVendorId(e.target.value)}>
              <option value="">Select a vendor…</option>
              {vendors.map((v) => (
                <option key={v.id} value={v.id}>
                  {v.brand_name || v.company_name}
                </option>
              ))}
            </select>
            {errorText('vendor') || (
              <small className="hint">This vendor is billed what Prozo charges.</small>
            )}
          </label>
        )}
      </div>

      <div className="courierHeader">
        <h4 className="courierHeading">
          Select Courier{!quotesLoading && quotes.length ? ` (${quotes.length})` : ''}
        </h4>
        <RefreshRatesButton onClick={fetchQuotes} loading={quotesLoading} />
      </div>

      {quotesError && !quotesLoading && <p className="fieldError">{quotesError}</p>}
      {!quotesLoading && !quotesError && errorText('courier')}

      {quotesLoading && (
        <>
          <p className="quotesLoadingNote">Pricing the lane…</p>
          <QuotesSkeleton />
        </>
      )}

      {/* Cards are suppressed entirely while fetching, so the skeleton is never
          shown alongside a rate that is being replaced. */}
      <div className="costCardsGrid">
        {!quotesLoading && quotes.map((quote, index) => {
          const {
            parent,
            serviceName,
            serviceTypePrimary,
            dispatchModeLabel,
            basePrice,
            finalPrice,
            minTatDays,
            maxTatDays,
            codAvailable,
            channel,
          } = describeQuote(quote);
          // A quote that maps to no bookable channel is shown but cannot be
          // picked — sending Prozo a channel it does not accept would fail at
          // creation, after the operator thought they had chosen.
          const selectable = Boolean(channel);
          const isSelected = selectable && courier === channel;

          return (
            <div
              key={`${parent}-${dispatchModeLabel}-${index}`}
              className={`costCard ${isSelected ? 'costCardSelected' : ''}`}
            >
              <div className="costCardHeader">
                <div className="providerInfo">
                  <div className="providerName">{parent}</div>
                  <div className="serviceName">{serviceName}</div>
                </div>
                <div className="costCardPrice">
                  <div className="finalPrice">₹{Number(finalPrice).toFixed(2)}</div>
                  {basePrice !== finalPrice && (
                    <div className="basePrice">Base: ₹{Number(basePrice).toFixed(2)}</div>
                  )}
                </div>
              </div>

              <div className="costCardDetails">
                {(serviceTypePrimary || dispatchModeLabel) && (
                  <div className="detailRow">
                    <span className="detailLabel">Service:</span>
                    <span className="detailValue">
                      {serviceTypePrimary || '—'}
                      {dispatchModeLabel ? ` · ${dispatchModeLabel}` : ''}
                    </span>
                  </div>
                )}
                {(minTatDays > 0 || maxTatDays > 0) && (
                  <div className="detailRow">
                    <span className="detailLabel">Delivery Time:</span>
                    <span className="detailValue highlight">
                      {minTatDays === maxTatDays
                        ? `${minTatDays} day${minTatDays > 1 ? 's' : ''}`
                        : `${minTatDays}-${maxTatDays} days`}
                    </span>
                  </div>
                )}
                {codAvailable !== undefined && (
                  <div className="detailRow">
                    <span className="detailLabel">COD Available:</span>
                    <span className={`detailValue ${codAvailable ? 'success' : 'muted'}`}>
                      {codAvailable ? '✓ Yes' : '✗ No'}
                    </span>
                  </div>
                )}
              </div>

              <button
                type="button"
                className="selectProviderBtn"
                disabled={!selectable}
                onClick={() => setCourier(channel)}
              >
                {isSelected
                  ? '✓ Selected'
                  : selectable
                    ? 'Select This Option'
                    : 'Needs manual selection'}
              </button>
            </div>
          );
        })}
      </div>
    </>
  );

  const selectedVendor = vendors.find((v) => String(v.id) === String(vendorId));
  const renderReview = () => (
    <>
      <p className="manualStepHint">
        Check the order, then generate the label. Once the courier issues an AWB this order can no
        longer be edited — it can only be cancelled and cloned.
      </p>

      {/* The order number leads: it is the one value the operator invented, and
          the one they will search by afterwards. */}
      <div className="manualReviewTop">
        <div>
          <span className="manualReviewEyebrow">Order number</span>
          <span className="manualReviewOrderNo">{orderNumber || '—'}</span>
        </div>
        <span
          className={`manualBearerPill ${chargeBearer === 'BRAND' ? 'isBrand' : 'isSuspire'}`}
        >
          {chargeBearer === 'BRAND'
            ? `Brand · ${selectedVendor?.brand_name || selectedVendor?.company_name || 'vendor'}`
            : 'Suspire bears shipping'}
        </span>
      </div>

      {/* Route reads left-to-right, the way the parcel travels — far easier to
          scan than two address rows stacked in a label/value table. */}
      <div className="manualReviewRoute">
        <div className="manualRouteEnd">
          <span className="manualReviewEyebrow">Ship from</span>
          <strong>{pickupContact.name || '—'}</strong>
          <p>
            {shipFrom.address_line_1}
            {shipFrom.address_line_2 ? `, ${shipFrom.address_line_2}` : ''}
            <br />
            {shipFrom.city}, {shipFrom.state} {shipFrom.pincode}
          </p>
          {pickupContact.phone && <span className="manualRouteMeta">{pickupContact.phone}</span>}
        </div>

        <div className="manualRouteArrow" aria-hidden="true">
          <span className="manualRouteLine" />
          <span className="manualRouteChevron">›</span>
        </div>

        <div className="manualRouteEnd">
          <span className="manualReviewEyebrow">Ship to</span>
          <strong>{customer.name || '—'}</strong>
          <p>
            {shipTo.address_line_1}
            {shipTo.address_line_2 ? `, ${shipTo.address_line_2}` : ''}
            <br />
            {shipTo.city}, {shipTo.state} {shipTo.pincode}
          </p>
          {customer.phone && <span className="manualRouteMeta">{customer.phone}</span>}
        </div>
      </div>

      {/* Money and parcel as tiles: each is a single figure the operator checks
          at a glance, so they read better side by side than stacked in rows. */}
      <div className="manualReviewTiles">
        <div className="manualTile">
          <span className="manualReviewEyebrow">Payment</span>
          <strong>{paymentMode === 'COD' ? 'Cash on delivery' : 'Prepaid'}</strong>
          {paymentMode === 'COD' && (
            <span className="manualTileNote">Collect ₹{money(codAmount).toFixed(2)}</span>
          )}
        </div>
        <div className="manualTile">
          <span className="manualReviewEyebrow">Invoice value</span>
          <strong>₹{money(invoiceValue).toFixed(2)}</strong>
          <span className="manualTileNote">Declared ₹{declaredTotal.toFixed(2)}</span>
        </div>
        <div className="manualTile">
          <span className="manualReviewEyebrow">Package</span>
          <strong>{pkg.weight_g || 0} g</strong>
          <span className="manualTileNote">
            {pkg.length_cm}×{pkg.breadth_cm}×{pkg.height_cm} cm
          </span>
        </div>
        <div className="manualTile">
          <span className="manualReviewEyebrow">Courier</span>
          {/* `courier` is the bookable channel string, e.g. "bluedart-air". */}
          <strong className="manualTileChannel">{courier || 'Not selected'}</strong>
          <span className="manualTileNote">{lines.length} {lines.length === 1 ? 'line' : 'lines'}</span>
        </div>
      </div>

      {/* Surfaced rather than left to trust: the two figures are independent, so
          a typo in either is only catchable here. */}
      {Math.abs(money(invoiceValue) - declaredTotal) > 0.01 && (
        <p className="manualReviewWarn">
          Invoice value (₹{money(invoiceValue).toFixed(2)}) differs from the line-item total
          (₹{declaredTotal.toFixed(2)}). That is fine if it includes shipping or handling — worth a
          second look otherwise.
        </p>
      )}

      <div className="manualReviewLines">
        <div className="manualReviewLinesHead">
          <span className="manualReviewEyebrow">Line items</span>
        </div>
        <table className="manualReviewTable">
          <thead>
            <tr>
              <th>SKU</th>
              <th>Product</th>
              <th className="isNum">Qty</th>
              <th className="isNum">Unit</th>
              <th className="isNum">Total</th>
            </tr>
          </thead>
          <tbody>
            {lines.map((line, index) => (
              <tr key={index}>
                <td className="isMono">{line.sku || '—'}</td>
                <td>{line.product_name || '—'}</td>
                <td className="isNum">{line.quantity}</td>
                <td className="isNum">₹{money(line.unit_price).toFixed(2)}</td>
                <td className="isNum">
                  ₹{Math.max(
                    0,
                    money(line.unit_price) * (parseInt(line.quantity, 10) || 0) - money(line.discount)
                  ).toFixed(2)}
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <td colSpan={4}>Declared total</td>
              <td className="isNum">₹{declaredTotal.toFixed(2)}</td>
            </tr>
          </tfoot>
        </table>
      </div>

      <label className="field gridFull">
        <span>Notes *</span>
        <textarea
          rows={3}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Why is this order being raised? Internal only — never sent to the courier."
        />
        {!notes.trim() && (
          <small className="fieldError">
            A reason is required before the label can be generated.
          </small>
        )}
      </label>

      {error && <p className="fieldError">{error}</p>}
    </>
  );

  const renderStep = () => {
    if (step === 0) return renderCustomer();
    if (step === 1) return renderPickup();
    if (step === 2) return renderOrder();
    if (step === 3) return renderPackage();
    if (step === 4) return renderCourier();
    return renderReview();
  };

  return (
    <div className="labelsContainer manualWizard">
      <div className="labelsHeader">
        <h1 className="labelsTitle">{clonedFrom ? 'Clone Manual Order' : 'New Manual Order'}</h1>
        <p className="labelsSubtitle">
          An order placed outside Shopify. Complete each step, then generate the label.
        </p>
      </div>

      {/* Says what was carried over and what was not, so the operator is not
          left guessing why the courier and reason are blank. */}
      {clonedFrom && (
        <div className="banner bannerInfo">
          <strong>Cloning {clonedFrom.order_number}.</strong>{' '}
          Details are pre-filled and editable. The order number, courier and
          notes are deliberately left blank — each is a fresh decision for this
          shipment. Nothing is saved until you generate the label.
        </div>
      )}

      <div className="layout">
        <nav className="stepper" aria-label="Manual order steps">
          {STEPS.map((name, index) => (
            <button
              type="button"
              key={name}
              className={`step ${index === step ? 'stepCurrent' : ''} ${
                index < step ? 'stepDone' : ''
              }`}
              disabled={index > step}
              onClick={() => setStep(index)}
            >
              <span className="stepIndex">{index < step ? '✓' : index + 1}</span>
              {name}
            </button>
          ))}
        </nav>

        <section className="panel">
          <h2 className="panelTitle">
            {step + 1}. {STEPS[step]}
          </h2>
          <div className="panelBody">{renderStep()}</div>

          <div className="actions">
            {step > 0 && (
              <button type="button" className="btn btnGhost" onClick={() => setStep(step - 1)}>
                Back
              </button>
            )}
            {step < STEPS.length - 1 ? (
              <button type="button" className="btn btnPrimary" onClick={goNext}>
                Next
              </button>
            ) : (
              <button
                type="button"
                className="btn btnPrimary"
                disabled={!canSubmit}
                onClick={handleSubmit}
              >
                {submitting ? 'Generating…' : 'Generate Label'}
              </button>
            )}
          </div>
        </section>
      </div>
    </div>
  );
}

export default CreateManualOrder;
